import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthModal from '../AuthModal';
import './HomePricingModal.css';

interface HomePricingModalProps {
    isOpen: boolean;
    onClose: () => void;
}

// SVG Icons
const CheckIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
);

export function HomePricingModal({ isOpen, onClose }: HomePricingModalProps) {
    const { t } = useTranslation();
    const [showAuthModal, setShowAuthModal] = useState(false);

    if (!isOpen) return null;

    const handleCreateAccount = () => {
        setShowAuthModal(true);
    };

    const handleAuthClose = () => {
        setShowAuthModal(false);
        onClose();
    };

    const scrollToExplore = () => {
        onClose();
        setTimeout(() => {
            const exploreSection = document.getElementById('explore-section');
            if (exploreSection) {
                exploreSection.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    return (
        <>
            <div className="home-pricing-modal-overlay" onClick={onClose}>
                <div className="home-pricing-modal" onClick={(e) => e.stopPropagation()}>
                    {/* Close Button */}
                    <button className="modal-close-btn" onClick={onClose} aria-label={t('common.close')}>
                        <CloseIcon />
                    </button>

                    {/* Header */}
                    <header className="modal-header">
                        <h2 className="modal-title">{t('homePricing.title')}</h2>
                        <p className="modal-subtitle">{t('homePricing.subtitle')}</p>
                    </header>

                    {/* Cards */}
                    <div className="pricing-cards">
                        {/* Free Card - Highlighted */}
                        <div className="pricing-card pricing-card-free">
                            <h3 className="card-tier">{t('homePricing.free.title')}</h3>
                            <ul className="card-features">
                                <li><CheckIcon /><span>{t('homePricing.free.f1')}</span></li>
                                <li><CheckIcon /><span>{t('homePricing.free.f2')}</span></li>
                                <li><CheckIcon /><span>{t('homePricing.free.f3')}</span></li>
                                <li><CheckIcon /><span>{t('homePricing.free.f4')}</span></li>
                            </ul>
                            <p className="card-helper">{t('homePricing.free.helper')}</p>
                        </div>

                        {/* Paid Card - Unlock */}
                        <div className="pricing-card pricing-card-paid">
                            <div className="card-promo-badge">
                                <span>{t('homePricing.paid.badge')}</span>
                            </div>
                            <h3 className="card-tier">{t('homePricing.paid.title')}</h3>
                            <div className="card-pricing">
                                <span className="price-original">{t('homePricing.paid.price_original')}</span>
                                <span className="price-promo">{t('homePricing.paid.price_promo')}</span>
                            </div>
                            <p className="price-after">{t('homePricing.paid.price_after')}</p>
                            <ul className="card-features">
                                <li><CheckIcon /><span>{t('homePricing.paid.f1')}</span></li>
                                <li><CheckIcon /><span>{t('homePricing.paid.f2')}</span></li>
                                <li><CheckIcon /><span>{t('homePricing.paid.f3')}</span></li>
                                <li><CheckIcon /><span>{t('homePricing.paid.f4')}</span></li>
                            </ul>
                            <p className="card-helper">{t('homePricing.paid.helper')}</p>
                        </div>
                    </div>

                    {/* Primary CTA */}
                    <div className="modal-cta-section">
                        <button className="modal-primary-cta" onClick={handleCreateAccount}>
                            {t('homePricing.cta.primary')}
                        </button>
                        <p className="modal-cta-subtext">{t('homePricing.cta.subtext')}</p>
                    </div>

                    {/* Secondary Action */}
                    <button className="modal-secondary-cta" onClick={scrollToExplore}>
                        {t('homePricing.cta.secondary')}
                        <ArrowRightIcon />
                    </button>

                    {/* Footer Microcopy */}
                    <footer className="modal-footer">
                        <p>{t('homePricing.footer.line1')}</p>
                        <p>{t('homePricing.footer.line2')}</p>
                        <p className="footer-highlight">{t('homePricing.footer.line3')}</p>
                    </footer>
                </div>
            </div>

            {/* Nested Auth Modal */}
            <AuthModal 
                isOpen={showAuthModal} 
                onClose={handleAuthClose}
                redirectPath="/"
            />
        </>
    );
}

export default HomePricingModal;
